import type { ReactNode } from 'react';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import SelectableQuantityCounter from '../../../../components/ui/SelectableQuantityCounter/SelectableQuantityCounter';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import type { LotteryOptionGoodsForm } from '../../types';
import styles from './OptionNamedSelectableStepQuantityCollectionList.module.scss';

type OptionNamedSelectableStepQuantityCollectionListProps = {
  formItem: LotteryOptionGoodsForm;
  namedSelectableItems: NonNullable<LotteryOptionGoodsForm['optionInfo']['namedSelectable']>;
  isDeadlinePassed: boolean;
  selectedItems: Map<string, number>;
  inputErrors: Record<string, boolean>;
  onInputChange: (inputKey: string, value: string) => void;
  getNamedInputPlaceholder: () => string;
  invalidCharacterErrorMessage: string;
  stagingHeader: (formItem: LotteryOptionGoodsForm) => ReactNode;
};

function OptionNamedSelectableStepQuantityCollectionList({
  formItem,
  namedSelectableItems,
  isDeadlinePassed,
  selectedItems,
  inputErrors,
  onInputChange,
  getNamedInputPlaceholder,
  invalidCharacterErrorMessage,
  stagingHeader,
}: OptionNamedSelectableStepQuantityCollectionListProps) {
  const renderNamedSelectableQuantityItem = (
    namedSelectableItem: NonNullable<LotteryOptionGoodsForm['optionInfo']['namedSelectable']>[number],
    index: number,
  ) => {
    const isFirstNamedSelectableItem: boolean = index === 0;
    // 各景品の選択数量を取得
    const goodId = `${formItem.id}-${index}`;
    const selectedQuantity = selectedItems.get(goodId) || 0;

    // 選べるオプションの数量の初期値を作る関数
    const buildInitialQuantity = () => {
      // selectedItemsに存在する場合はその値を返す
      if (selectedItems.has(goodId)) return selectedQuantity;
      // サーバーから取得した項目が設定されていればその値を返す
      if (namedSelectableItem.value) return Number(namedSelectableItem.value);
      // それ以外は0を返す
      return 0;
    };
    const initialQuantity = buildInitialQuantity();

    const isAutoInput = () => {
      // 入力期限を迎えていない場合は非表示
      if (!isDeadlinePassed) return false;

      // システムインプットがされていない場合は非表示
      if (!namedSelectableItem.isSystemInput) return false;

      // value未設定の場合は非表示
      if (namedSelectableItem.value === '') return false;

      // valueが0以外なら表示、0なら非表示
      return namedSelectableItem.value !== '0';
    };

    // 期限切れの場合のスタイルを取得する関数
    const getWrapperQuantityPastDeadlineClassName = () => {
      if (isAutoInput() || isDeadlinePassed) return styles.wrapperQuantityPastDeadline;
      return '';
    };

    const namedSelectableImageUrl = 'imageUrl' in namedSelectableItem ? namedSelectableItem.imageUrl : undefined;
    const items: ReactNode[] = [];

    if (isFirstNamedSelectableItem) {
      items.push(
        <div
          key={`selectable-header-${formItem.id}`}
          className={`${styles.itemCardHeaderQuantity} ${styles.optionItemGroupQuantityHeaderItem}`}
        >
          <div className={styles.itemCardHeaderQuantityInner}>
            <div className={`${styles.itemDetailHeaderQuantityWrapper}`}>
              <div className={`${styles.itemDetailHeaderQuantity} ${utilsStyles.mlSmall}`}>
                <div className={styles.stagingRankHeaderQuantityWrapper}>{stagingHeader(formItem)}</div>
                <div className={styles.itemNameHeaderQuantityWrapper}>
                  <p className={`${styles.itemNameHeaderQuantity} ${utilsStyles.textBold}`}>{formItem.label.name}</p>
                </div>
                <div className={styles.itemNameHeaderQuantityWrapper}>
                  <p className={`${styles.itemNameHeaderQuantity} ${utilsStyles.textBold}`}>{formItem.name}</p>
                </div>
                <div className={styles.acquiredCountHeaderQuantityWrapper}>
                  <p className={`${styles.acquiredCountHeaderQuantity}`}>保有数：{formItem.totalAcquiredCount}個</p>
                </div>
              </div>
            </div>
          </div>
        </div>,
      );
    }

    items.push(
      <div
        key={`selectable-${formItem.id}-${index}`}
        className={`${styles.itemCardQuantity} ${styles.optionItemGroupQuantityItem}`}
      >
        <div className={styles.itemCardQuantityInner}>
          <div className={`${styles.itemDetailQuantityWrapper} ${utilsStyles.mtXSmall}`}>
            <div className={styles.itemImageQuantity}>
              <ImageView
                alt={namedSelectableItem.label || formItem.name}
                src={namedSelectableImageUrl || formItem.imageUrl}
              />
            </div>
            <div className={`${styles.itemDetailQuantity} ${utilsStyles.mlSmall}`}>
              <div className={styles.itemNameWrapper}>
                <p className={`${styles.itemNameQuantity} ${utilsStyles.textBold}`}>
                  {namedSelectableItem.label || 'オプション選択項目'}
                </p>
              </div>
              <div className={`${styles.wrapperQuantity} ${getWrapperQuantityPastDeadlineClassName()}`}>
                <div className={`${utilsStyles.centered} ${styles.quantityCounterWrapper}`}>
                  <SelectableQuantityCounter
                    goodId={`${formItem.id}-${index}`}
                    acquiredCount={formItem.totalAcquiredCount}
                    disabled={false}
                    tradeableInCount={formItem.totalAcquiredCount}
                    totalSelectedInLabel={Array.from(selectedItems.values()).reduce(
                      (sum, current) => sum + current,
                      0,
                    )}
                    initialQuantity={initialQuantity}
                    isPastDeadline={isDeadlinePassed}
                    isAutoInput={isAutoInput()}
                  />
                </div>
              </div>
              <div className={styles.autoInputMessageQuantityWrapper}>
                {isAutoInput() && (
                  <div
                    className={`${utilsStyles.textSmall} ${utilsStyles.textBold} ${styles.autoInputMessage} ${styles.autoInputMessageRight}`}
                  >
                    自動入力数：{namedSelectableItem.value}個
                  </div>
                )}
              </div>
            </div>
          </div>
          {isDeadlinePassed && (
            <>
              {/* 期限切れの場合、2ページ目は進めないため、例外的に1ページ目に宛名の入力欄を表示(編集不可) */}
              <div className={styles.optionInputNamedWrapper}>
                {Array.from({ length: selectedQuantity }, (_, inputIndex) => {
                  // 既存の入力内容を取得（namedSelectableのnotesから）
                  const noteItem = namedSelectableItem.notes?.[inputIndex];
                  const existingValue = noteItem?.value || '';
                  const isSystemInput = noteItem?.isSystemInput || false;
                  const inputKey = `named_selectable-${formItem.id}-${index}-${inputIndex}`;
                  const hasError = inputErrors[inputKey];
                  const buildDefaultValue = () => {
                    if (!isDeadlinePassed) return existingValue;
                    if (!isSystemInput) return existingValue;
                    if (!existingValue) return existingValue;
                    return `【自動入力】${existingValue}`;
                  };

                  return (
                    <div key={`${formItem.id}-${index}-${inputIndex}`} className={styles.inputContainer}>
                      {hasError && <div className={styles.errorMessage}>{invalidCharacterErrorMessage}</div>}
                      <textarea
                        key={`input-${formItem.id}-${index}-${inputIndex}-${existingValue}`}
                        name={`optionInput-${formItem.id}-${index}-${inputIndex}`}
                        placeholder={getNamedInputPlaceholder()}
                        className={`${styles.optionInput} ${isDeadlinePassed ? styles.editMode : ''} ${hasError ? styles.inputError : ''} ${isDeadlinePassed && isSystemInput ? styles.systemInput : ''}`}
                        defaultValue={buildDefaultValue()}
                        onChange={(e) => onInputChange(inputKey, e.target.value)}
                        disabled={true}
                        rows={3}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>,
    );

    return items;
  };

  return <>{namedSelectableItems.flatMap(renderNamedSelectableQuantityItem)}</>;
}

export default OptionNamedSelectableStepQuantityCollectionList;
